import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "../icons";
import { CATEGORY_LABELS, CATEGORY_SUBCATEGORIES } from "../../types";
import { MAIN_CATEGORIES } from "./filterUtils";
import InStockSwitch from "./InStockSwitch";
import type useCatalogFilters from "./useCatalogFilters";
import { useLanguage } from "../../context/LanguageContext";

type Filters = ReturnType<typeof useCatalogFilters>;

export default function CatalogSidebar({
  filters,
  brandPickerAlignBottomY = null,
}: {
  filters: Filters;
  brandPickerAlignBottomY?: number | null;
}) {
  const { t } = useLanguage();
  const {
    activeCategory,
    setActiveCategory,
    activeSubcategory,
    setSubcategory,
    inStockOnly,
    setInStockOnly,
    inStockCount,
    categoryCounts,
    subcategoryCounts,
    specFieldDefs,
    numericBounds,
    numericFilters,
    setNumericFilter,
    selectOptions,
    selectOptionCounts,
    selectFilters,
    toggleSelectOption,
    brandOptions,
    brandCounts,
    selectedBrands,
    toggleBrand,
  } = filters;

  const hasCharacteristics =
    specFieldDefs.length > 0 &&
    (Object.keys(numericBounds).length > 0 || Object.keys(selectOptions).length > 0);

  // Same rule as the Mechanical subcategory filtering below, applied to the
  // top-level list too: a category with nothing in it yet (e.g. Tableware,
  // still empty) is just noise in the facet list — hide it until a product
  // actually lands there instead of showing an empty, unclickable-feeling row.
  const visibleCategories = MAIN_CATEGORIES.filter((category) => (categoryCounts[category] ?? 0) > 0);
  // A category's taxonomy is often built out ahead of the catalog filling
  // in — hide subcategories with no products yet rather than showing empty
  // facet options, until each one has at least one real product.
  const activeSubcategories =
    activeCategory !== "All"
      ? (CATEGORY_SUBCATEGORIES[activeCategory] ?? []).filter((sub) => (subcategoryCounts[sub] ?? 0) > 0)
      : [];

  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const scrollDirectionRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);
  // The category column is height-bounded (a category with many
  // subcategories expanded can run long) — hovering near the top or bottom
  // edge auto-scrolls that direction (continues as long as the pointer
  // stays in the zone) to reveal what's cut off, since there's no visible
  // scrollbar or arrow buttons to hint at more content otherwise.
  const EDGE_ZONE_PX = 48;
  const SCROLL_SPEED_PX = 6;

  const stopCategoryAutoScroll = () => {
    scrollDirectionRef.current = 0;
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };

  const startCategoryAutoScroll = (direction: 1 | -1) => {
    scrollDirectionRef.current = direction;
    if (scrollRafRef.current !== null) return;
    const step = () => {
      const el = categoryScrollRef.current;
      if (!el || scrollDirectionRef.current === 0) {
        scrollRafRef.current = null;
        return;
      }
      el.scrollTop += scrollDirectionRef.current * SCROLL_SPEED_PX;
      scrollRafRef.current = requestAnimationFrame(step);
    };
    scrollRafRef.current = requestAnimationFrame(step);
  };

  const handleCategoryColumnMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const { top, height } = el.getBoundingClientRect();
    const y = e.clientY - top;
    if (y < EDGE_ZONE_PX) startCategoryAutoScroll(-1);
    else if (y > height - EDGE_ZONE_PX) startCategoryAutoScroll(1);
    else stopCategoryAutoScroll();
  };

  useEffect(() => stopCategoryAutoScroll, []);

  return (
    <div className="flex flex-col gap-7">
      {/* Lives in the always-visible toolbar at lg+ instead — this copy is
          suppressed there via lg:hidden and only actually reachable through
          the mobile filter drawer, which is itself lg:hidden on its parent
          <aside>, so the two never show at once. */}
      <div className="lg:hidden">
        <InStockSwitch checked={inStockOnly} count={inStockCount} onChange={setInStockOnly} />
      </div>

      <FacetSection title={t("categoryFacetTitle")} defaultOpen>
        <div
          ref={categoryScrollRef}
          onMouseMove={handleCategoryColumnMouseMove}
          onMouseLeave={stopCategoryAutoScroll}
          className="no-scrollbar flex max-h-[520px] touch-pan-y flex-col gap-0.5 overflow-y-auto"
        >
          <CategoryTab
            label={t("recommendedLabel")}
            active={activeCategory === "All"}
            onClick={() => setActiveCategory("All")}
          />
          {visibleCategories.map((category) => {
            const isActive = activeCategory === category;
            const subcategories = isActive ? activeSubcategories : [];
            return (
              <div key={category}>
                <CategoryTab
                  label={CATEGORY_LABELS[category]}
                  count={categoryCounts[category] ?? 0}
                  active={isActive}
                  onClick={() => setActiveCategory(category)}
                />
                {subcategories.length > 0 && (
                  <SubcategoryList
                    subcategories={subcategories}
                    counts={subcategoryCounts}
                    activeSubcategory={activeSubcategory}
                    onSelect={setSubcategory}
                  />
                )}
              </div>
            );
          })}
        </div>
      </FacetSection>

      {brandOptions.length > 0 && (
        <FacetSection title={t("brandFacetTitle")} defaultOpen>
          <BrandPicker
            options={brandOptions.map((b) => ({ value: b, label: b, count: brandCounts[b] ?? 0 }))}
            selected={selectedBrands}
            onToggle={toggleBrand}
            alignBottomY={brandPickerAlignBottomY}
          />
        </FacetSection>
      )}

      {hasCharacteristics && (
        <FacetSection title={t("characteristicsFacetTitle")} defaultOpen>
          <div className="flex flex-col gap-6">
            {specFieldDefs.map((field) => {
              if (field.type === "number" && numericBounds[field.key]) {
                const bounds = numericBounds[field.key];
                const current = numericFilters[field.key] ?? {};
                return (
                  <div key={field.key} className="flex flex-col gap-2">
                    <p className="text-sm text-steel-600">
                      {field.label}
                      {field.unit ? `, ${field.unit}` : ""}
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder={String(bounds.min)}
                        value={current.min ?? ""}
                        onChange={(e) =>
                          setNumericFilter(field.key, {
                            ...current,
                            min: e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                        className="w-full border border-steel-200 px-2.5 py-2 text-sm text-ink outline-none focus:border-ink"
                      />
                      <span className="text-steel-300">–</span>
                      <input
                        type="number"
                        placeholder={String(bounds.max)}
                        value={current.max ?? ""}
                        onChange={(e) =>
                          setNumericFilter(field.key, {
                            ...current,
                            max: e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                        className="w-full border border-steel-200 px-2.5 py-2 text-sm text-ink outline-none focus:border-ink"
                      />
                    </div>
                  </div>
                );
              }
              if (field.type === "select" && selectOptions[field.key]) {
                return (
                  <div key={field.key} className="flex flex-col gap-2.5">
                    <p className="text-sm text-steel-600">{field.label}</p>
                    <CheckboxList
                      options={selectOptions[field.key].map((o) => ({
                        value: o,
                        label: o,
                        count: selectOptionCounts[field.key]?.[o] ?? 0,
                      }))}
                      selected={selectFilters[field.key] ?? new Set()}
                      onToggle={(o) => toggleSelectOption(field.key, o)}
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </FacetSection>
      )}
    </div>
  );
}

function FacetSection({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between border-b border-steel-300 pb-3 text-xs font-semibold uppercase tracking-wide2 text-steel-500"
      >
        {title}
        <ChevronDown
          size={14}
          className={`shrink-0 text-steel-500 transition-transform duration-200 ease-engineered ${open ? "rotate-180" : ""}`}
        />
      </button>
      {/* Grid-rows fold (0fr/1fr), same technique as Navbar's mobile menu
          and top-bar collapse — replaces native <details>, which snaps
          open/closed with no way to animate the height. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-engineered ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Horizontal tab row for top-level categories — replaces the old vertical
// sidebar list now that this whole facet lives under the toolbar instead of
// in a left column. Same horizontal-scrolling tab-slider treatment as the
// product page's characteristics/description tabs (see ProductDetailPage's
// tab row) rather than wrapping to multiple lines — a single row that
// scrolls instead of growing taller as more categories exist. Active
// subcategories render as a second row of pills beneath this one (see the
// main render), not part of this slider.
function CategoryTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        onClick();
        // A category with many subcategories can push the active row out of
        // the scroll column's bounded height — without this it can land
        // partially clipped at the edge instead of scrolling fully into view.
        e.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }}
      className={`flex w-full items-center justify-between gap-2 border-l-2 py-1.5 pl-3 pr-2 text-left text-sm transition-colors duration-200 ease-engineered ${
        active
          ? "border-accent font-semibold text-accent"
          : "border-transparent text-steel-600 hover:text-ink"
      }`}
    >
      <span>{label}</span>
      {count !== undefined && <span className="text-xs text-steel-400">{count}</span>}
    </button>
  );
}

// Subcategory list under the active top-level category. The grey rule is a
// single unbroken line the full height of the list (not per-row borders,
// which would gap at the row spacing) — the red segment is a separate layer
// on top that slides to the active row's measured position, so switching
// subcategories reads as the marker moving rather than repainting. Same
// 2px stroke width as the parent CategoryTab's border-l-2 so the two read
// as one consistent indicator language.
function SubcategoryList({
  subcategories,
  counts,
  activeSubcategory,
  onSelect,
}: {
  subcategories: string[];
  counts: Record<string, number>;
  activeSubcategory: string | null;
  onSelect: (sub: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ top: number; height: number } | null>(null);

  useEffect(() => {
    const activeEl = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (activeEl) {
      setIndicator({ top: activeEl.offsetTop, height: activeEl.offsetHeight });
    }
  }, [subcategories, activeSubcategory]);

  return (
    <div ref={listRef} className="relative ml-3 flex flex-col gap-0.5 pl-3">
      <div className="absolute inset-y-0 left-0 w-0.5 bg-steel-200" />
      {indicator && (
        <div
          className="absolute left-0 w-0.5 bg-accent transition-all duration-200 ease-engineered"
          style={{ top: indicator.top, height: indicator.height }}
        />
      )}
      {subcategories.map((sub) => {
        const active = activeSubcategory === sub;
        return (
          <button
            key={sub}
            type="button"
            data-active={active}
            onClick={() => onSelect(sub)}
            className={`flex items-center justify-between gap-2 py-1.5 pr-2 text-left text-[13px] transition-colors duration-200 ease-engineered ${
              active ? "font-semibold text-accent" : "text-steel-600 hover:text-ink"
            }`}
          >
            <span>{sub}</span>
            <span className="text-xs text-steel-400">{counts[sub] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

function CheckboxList({
  options,
  selected,
  onToggle,
  maxVisible = 8,
}: {
  options: Array<{ value: string; label: string; count: number }>;
  selected: Set<string>;
  onToggle: (value: string) => void;
  maxVisible?: number;
}) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  // Search box only earns its space once the list is long enough that
  // scanning it is actually work — the same threshold that would otherwise
  // trigger the "show more" toggle, so the two never fight for the same
  // list. While searching, the maxVisible cap and expand/collapse toggle
  // both step aside: every match should be visible immediately.
  const showSearch = options.length > maxVisible;
  const isSearching = showSearch && query.trim().length > 0;
  const filteredOptions = isSearching
    ? options.filter((opt) => opt.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;
  // Split into an always-visible head and a foldable tail instead of
  // slicing one combined list — the tail then gets its own grid-rows fold
  // below, so the extra options animate open/closed instead of popping in
  // the instant "expanded" flips. Only relevant outside search, where
  // every match should already be visible with nothing left to fold.
  const primaryOptions = isSearching ? filteredOptions : filteredOptions.slice(0, maxVisible);
  const restOptions = isSearching ? [] : filteredOptions.slice(maxVisible);

  const renderOption = (opt: { value: string; label: string; count: number }) => {
    const isChecked = selected.has(opt.value);
    const isDisabled = opt.count === 0 && !isChecked;
    return (
      <label
        key={opt.value}
        className={`flex cursor-pointer items-center gap-2 text-sm ${
          isDisabled ? "text-steel-300" : "text-steel-600"
        }`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggle(opt.value)}
          className="accent-accent"
        />
        {opt.label}
        <span
          className={`relative -top-1.5 ml-0.5 align-baseline text-[10px] font-normal ${
            isDisabled ? "text-steel-300" : "text-accent"
          }`}
        >
          {opt.count}
        </span>
      </label>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {showSearch && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("brandSearchPlaceholder")}
          className="mb-1 w-full border border-steel-200 px-2.5 py-1.5 text-sm text-ink outline-none focus:border-ink"
        />
      )}
      {isSearching && filteredOptions.length === 0 && (
        <p className="text-sm text-steel-400">{t("brandSearchNoResults")}</p>
      )}
      {primaryOptions.map(renderOption)}
      {restOptions.length > 0 && (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-engineered ${
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="flex flex-col gap-2 overflow-hidden">{restOptions.map(renderOption)}</div>
        </div>
      )}
      {!isSearching && options.length > maxVisible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 self-start text-xs text-steel-400 transition-colors duration-200 ease-engineered hover:text-accent"
        >
          {expanded ? t("collapseFacet") : t("expandFacet")}
        </button>
      )}
    </div>
  );
}

// Always-visible wrapping grid of brand toggle chips — every brand is right
// there to see and click, no trigger to press and no panel hiding them
// first. Lives in the sidebar under the category list, so it wraps and
// scrolls vertically (not sideways) within that narrow column.
//
// The scroll indicator on the left is a custom-built rail + thumb, not the
// native browser scrollbar — Safari's overlay scrollbars only render during
// an active scroll/hover gesture, so a real `overflow` scrollbar is
// invisible in the resting state no matter how it's styled. This one is
// just two absolutely-positioned bars (grey track always full-height, red
// thumb sized/positioned from scrollTop via onScroll) so it reads as a
// permanent progress indicator, matching the 2px stroke of the active-
// category marker (border-l-2) next to "Все товары" above it.
export function BrandPicker({
  options,
  selected,
  onToggle,
  alignBottomY = null,
}: {
  options: Array<{ value: string; label: string; count: number }>;
  selected: Set<string>;
  onToggle: (value: string) => void;
  // Viewport Y of the product grid's last-row bottom edge (see
  // CatalogPage) — when set, the scroll box's own bottom edge is pinned to
  // that line instead of the fixed fallback height, so the brand column and
  // the product grid visually end together. Null below the lg breakpoint,
  // where the sidebar stacks above the grid instead of sitting beside it.
  alignBottomY?: number | null;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number } | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const sortedOptions = [...options].sort((a, b) => {
    const aSelected = selected.has(a.value);
    const bSelected = selected.has(b.value);
    if (aSelected === bSelected) return 0;
    return aSelected ? -1 : 1;
  });

  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb(null);
      return;
    }
    const height = Math.max(24, (clientHeight / scrollHeight) * clientHeight);
    const maxTop = clientHeight - height;
    const top = maxTop <= 0 ? 0 : (scrollTop / (scrollHeight - clientHeight)) * maxTop;
    setThumb({ top, height });
  };

  // Re-measure whenever the chip set changes (brand list loads, filters
  // narrow the counts) as well as on resize — a ResizeObserver on the
  // scroll container catches both content-height and viewport changes.
  useLayoutEffect(() => {
    updateThumb();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedOptions.length]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || alignBottomY == null) {
      setMaxHeight(null);
      return;
    }
    // A minimum floor keeps at least a row or two of chips visible/scrollable
    // even if the product grid ends up shorter than the brand column's top —
    // e.g. very few results, or a page with only one row of cards.
    const update = () => setMaxHeight(Math.max(120, alignBottomY - el.getBoundingClientRect().top));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [alignBottomY]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative pl-4">
        <div className="pointer-events-none absolute left-0 top-0 h-full w-0.5 bg-steel-200" />
        {thumb && (
          <div
            className="pointer-events-none absolute left-0 w-0.5 bg-accent"
            style={{ top: thumb.top, height: thumb.height }}
          />
        )}
        <div
          ref={scrollRef}
          onScroll={updateThumb}
          className="no-scrollbar overflow-y-auto"
          style={{ maxHeight: maxHeight ?? 420 }}
        >
          <div className="flex flex-wrap content-start gap-1.5 pb-0.5">
            {sortedOptions.map((opt) => {
              const isChecked = selected.has(opt.value);
              const isDisabled = opt.count === 0 && !isChecked;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => onToggle(opt.value)}
                  className={`flex items-center border px-2.5 py-1.5 text-left text-sm transition-colors duration-200 ease-engineered ${
                    isDisabled
                      ? "cursor-not-allowed border-steel-100 text-steel-300"
                      : isChecked
                        ? "border-accent bg-accent text-white"
                        : "border-steel-200 text-steel-600 hover:border-steel-400 hover:text-ink"
                  }`}
                >
                  <span>{opt.label}</span>
                  <span
                    className={`relative -top-1.5 ml-0.5 align-baseline text-[10px] font-normal ${
                      isChecked ? "text-white" : isDisabled ? "text-steel-300" : "text-accent"
                    }`}
                  >
                    {opt.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
